<script setup lang="ts">
import { isEqual } from "lodash-es";
import { computed, ref, watch } from "vue";

import { getBoardEventLog } from "@/backend/read/BackendReader";
import SidePanelContent from "@/components/SidePanel/SidePanelContent.vue";
import ComplexList from "@/components/ui/ComplexList/ComplexList.vue";
import ListBanner from "@/components/ui/ComplexList/ListBanner.vue";
import ListEmpty from "@/components/ui/ComplexList/ListEmpty.vue";
import LoadingIndicator from "@/components/ui/LoadingIndicator.vue";
import { runMinDelay, useSyncedTask } from "@/composables/useSyncedTask";
import type { ChangeTypeItem, StickyChange } from "@/model/change";
import { useActivityStore } from "@/store/activity";
import { useBoardStore } from "@/store/board";
import type { DateItem } from "@/store/helpers/activityDateFilters";
import { fadeOutAllCardMarks, resetAllCardMarks, resetCardMark } from "@/store/helpers/mark";

import ChangeTypesFilter from "./internal/ChangeTypesFilter.vue";
import ChangesByDay from "./internal/ChangesByDay.vue";

const store = useActivityStore();
const changes = ref<StickyChange[]>([]);
const { loading, runSync } = useSyncedTask();
const moreResults = ref<boolean>();

const changeTypes = computed({
  get: () => store?.selectedChangeTypes,
  set: (values: ChangeTypeItem[]) => store.selectChangeTypes(values),
});

watch(
  () => ({ ...store.allFilters }),
  (newState, prevState) => {
    // somehow the watch triggers sometimes even if neither board nor date change
    // check it to avoid multiple calls to the backend
    if (!isEqual(newState, prevState)) {
      void loadData(newState.date, newState.changeTypes);
    }
  },
  { immediate: true },
);

async function loadData(date: DateItem, changeTypes: string[]) {
  // Collect the iteration and type filters, if any
  const filters = useActivityStore().serverFilters;
  const loadEvents = getBoardEventLog(
    useBoardStore().currentBoard().id,
    date.fromDate,
    date.toDate,
    filters,
    [...changeTypes, "!a_center", "!stack_pos"],
  );
  // wait until ToggleButton finishes transition to not disturb the animation
  await runSync(
    () => runMinDelay(loadEvents, 300),
    (events) => {
      changes.value = events.changes;
      moreResults.value = events.moreChanges;
      updateHighlightedCards();
    },
  );
}

/**
 * Highlight the cards that match the current filters
 */
function updateHighlightedCards() {
  const board = useBoardStore().board;

  if (board && store.board && store.selectedDate && !loading.value) {
    fadeOutAllCardMarks(board);
    for (const change of changes.value) {
      resetCardMark(board.cards[change.stickyId]);
    }
  } else if (board) {
    resetAllCardMarks(board);
  }
}
</script>

<template>
  <SidePanelContent class="board-log-panel">
    <template #sub-title>
      <div class="panel-header">
        <h2 class="subtitle">{{ $t("boardDiff.log.title") }}</h2>
        <p class="description">{{ $t("boardDiff.log.description") }}</p>
        <ChangeTypesFilter v-model="changeTypes" />
      </div>
    </template>

    <ComplexList>
      <!-- Changes, grouped by day -->
      <ChangesByDay :changes show-cards />
      <!-- 'More results found - refine your search to see them' -->
      <ListBanner v-if="moreResults">{{ $t("boardDiff.log.moreResults") }}</ListBanner>

      <!-- Loading Spinner -->
      <ListEmpty v-if="loading">
        <LoadingIndicator />
      </ListEmpty>

      <!-- Empty List Message -->
      <ListEmpty v-if="changes.length === 0 && !loading">
        {{ $t("searchResults.noResults") }}
      </ListEmpty>
    </ComplexList>
  </SidePanelContent>
</template>
